/**
 * Tax & Pricing Settings — cached, module-level shared state used by the
 * Fuel Pricing Calculator. Reads from `/api/settings/tax-pricing` which
 * is the single source of truth for every fuel pricing calculation.
 *
 * Governance: OPS-002. Every calculation MUST read from this hook —
 * hard-coded HST rate, road tax, or carbon tax is a code-review reject.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const DEFAULTS = {
  ontario_road_tax: 0.147,
  hst_rate: 0.13,
  federal_carbon_tax: 0.176,
  carbon_tax_enabled: true,
  decimal_precision: 4,
  selling_price_rounding_rule: "ends_in_nine",
};

let CACHE = null;
let LOADING = null;

export async function loadTaxPricing() {
  if (CACHE) return CACHE;
  if (!LOADING) {
    LOADING = api.get("/settings/tax-pricing")
      .then((r) => {
        CACHE = { ...DEFAULTS, ...(r.data || {}) };
        return CACHE;
      })
      .catch(() => {
        CACHE = { ...DEFAULTS };
        return CACHE;
      })
      .finally(() => { LOADING = null; });
  }
  return LOADING;
}

export function useTaxPricing() {
  const [settings, setSettings] = useState(CACHE || DEFAULTS);
  useEffect(() => {
    let alive = true;
    loadTaxPricing().then((s) => { if (alive) setSettings(s); });
    return () => { alive = false; };
  }, []);
  return settings;
}

export function invalidateTaxPricing() {
  CACHE = null;
}

/**
 * Apply the configured rounding rule to a computed selling price. Never
 * mutates raw computations — only used at the presentation boundary.
 */
export function applyRoundingRule(value, rule, decimals) {
  if (!Number.isFinite(value)) return value;
  if (rule === "nearest_cent") {
    return Math.round(value * 100) / 100;
  }
  if (rule === "ends_in_nine") {
    // Force the last displayed digit at the configured precision to be 9.
    const factor = 10 ** decimals;
    const truncated = Math.floor(value * factor) / factor;
    return truncated + 9 / factor;
  }
  return value;
}

export function fmtFuel(n, decimals) {
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(decimals);
}
